import { ReactElement, useState, useEffect, useRef } from "react";
import Datepicker from "../../../../components/Shared/Datepicker";
import { useDependency } from "../../../../contexts/DependencyContext";
import { CalendarDate } from "../../../../services/datepicker/interfaces/calendar-date";
import { DateService } from "../../../../services/types/DateService";
import { className } from "../../../../shared/helpers";
import styles from "./DateFilters.module.scss";

export interface IDateRange
{
    from: Date | null;
    to: Date | null;
}

interface IDateFiltersProps
{
    onApply: (dateRanges: IDateRange[]) => void;
}

function DateFilters({ onApply }: IDateFiltersProps): ReactElement
{
    const [dateRange, setDateRange] = useState<IDateRange>({ from: null, to: null });
    const [beginDate, setBeginDate] = useState<IDateRange>({ from: null, to: null })
    const initDates = useRef<IDateRange>({ from: null, to: null });
    const initCompareDates = useRef<IDateRange>({ from: null, to: null });
    const [compareDateRange, setCompareDateRange] = useState<IDateRange>({ from: new Date(), to: new Date() });
    const [compareRanges, setCompareRanges] = useState(false);
    const service = useDependency(DateService);
    const [initialized, setInitialized] = useState(false);

    useEffect(() =>
    {
        const dateFrom = new Date();
        dateFrom.setDate(dateFrom.getDate() - 7);
        const dateTo = new Date();

        initDates.current = {
            from: dateFrom,
            to: dateTo
        };
        setDateRange({
            from: dateFrom,
            to: dateTo
        });
        setBeginDate({
            from: dateFrom,
            to: dateTo
        });
    }, [service]);

    useEffect(() =>
    {
        if (initialized || dateRange.from === null || dateRange.to === null) return;

        onApply([dateRange]);
        setInitialized(true);
    }, [dateRange, initialized, onApply])

    const toggleCompareRanges = () =>
    {
        const newCompareRanges = !compareRanges;

        if (newCompareRanges) setCompareDateRange({ from: null, to: null });

        setCompareRanges(newCompareRanges);
    }

    function applyFilters()
    {
        const ranges = [dateRange];

        if (compareRanges)  
        {
            ranges.push(compareDateRange);
            initCompareDates.current = compareDateRange;
        }

        initDates.current = dateRange;
        onApply(ranges);
    }

    function clearFilters()
    {
        const dateFrom = beginDate.from;
        const dateTo = beginDate.to;

        setDateRange({
            from: dateFrom,
            to: dateTo
        });
        
        if(compareRanges)
        {
            toggleCompareRanges();
        }

        if(initDates.current.from !== beginDate.from || initDates.current.to !== beginDate.to)
        {
            initDates.current = {
                from: dateFrom,
                to: dateTo
            };
            onApply( [{from: dateFrom, to: dateTo}] as IDateRange[]);
        }
    }

    function pickDateRange(key: string, date: CalendarDate): void
    {
        if (date)
        {
            const { day, month, year, hours, minutes, seconds } = date;

            setDateRange({
                ...dateRange,
                [key]: new Date(year, month, day, hours, minutes, seconds)
            });
        }
    }

    function pickCompareDateRange(key: string, date: CalendarDate): void
    {
        if (date)
        {
            const { day, month, year, hours, minutes, seconds } = date;

            setCompareDateRange({
                ...compareDateRange,
                [key]: new Date(year, month, day, hours, minutes, seconds)
            });
        }
    }

    const buttonIsDisabled = initDates.current.from === dateRange.from && initDates.current.to === dateRange.to && 
          ( compareRanges && compareDateRange.from === initCompareDates.current.from && compareDateRange.to === initCompareDates.current.to);

    return (
        <section className={styles.wrapper}>
            <p>Select date range</p>

            <div className={styles.dateWrapper}>
                <Datepicker
                    customClassName={styles.datepicker}
                    currentDate={dateRange.from as Date}
                    pickDate={date => pickDateRange("from", date)}
                />

                <Datepicker
                    customClassName={`${styles.datepicker} ${styles.rightDatepicker}`}
                    currentDate={dateRange.to as Date}
                    pickDate={date => pickDateRange("to", date)}
                />
            </div>

            <label className="custom-check-wrap">
                <input
                    type="checkbox"
                    className="custom-hidden"
                    onChange={toggleCompareRanges}
                    checked={compareRanges}
                />

                <i {...className("material-icons", "mi-18", "custom-checkbox")} />
                <span>Compare with another range</span>
            </label>

            {compareRanges && (
                <div className={styles.dateWrapper}>
                    <Datepicker
                        customClassName={styles.datepicker}
                        currentDate={compareDateRange.from ? compareDateRange.from : new Date()}
                        pickDate={(date) => pickCompareDateRange("from", date)}
                    />

                    <Datepicker
                        customClassName={`${styles.datepicker} ${styles.rightDatepicker}`}
                        currentDate={compareDateRange.to ? compareDateRange.to : new Date()}
                        pickDate={(date) => pickCompareDateRange("to", date)}
                    />
                </div>
            )}

            <button
                {...className("btn", `${buttonIsDisabled ? "btn-offline" : "btn-info-light"}`, styles.applyFilterButton)}
                onClick={applyFilters}
                disabled={buttonIsDisabled}
            >
                <span>Apply</span>
            </button>

            <button
                {...className("btn", `"btn-info-light"}`, styles.applyFilterButton)}
                onClick={clearFilters}
            >
                <span>Reset</span>
            </button>
        </section>
    );
}

export default DateFilters;